"use client";

import { createTheme, alpha } from "@mui/material";
import { grey as MuiGrey } from "@mui/material/colors";
import { COLOR_SCALE } from "@equipmentshare/t3-design-tokens";
import { defaultThemeOptions } from "@equipmentshare/t3-mui-theme";

const DEFAULT_RADIUS_SIZE = 6;

/**
 * Values sourced from T3 Design Guidelines
 *
 * {@link https://design.estrack.com/9442d239a/p/26bbdb--foundation Visit Foundation}.
 */

// Define the theme options with our extended properties
// The module augmentations in mui-augmentations.d.ts extend the base MUI types
// We use a type assertion here because the consuming package may not see the augmentations
// during type checking, but the types are correct at runtime
const themeOptions = {
  ...defaultThemeOptions,

  typography: {
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Arial, sans-serif",
    h1: {
      fontSize: "2rem",
      fontWeight: 500,
      lineHeight: 1.3,
    },
    h2: {
      fontSize: "1.75rem",
      fontWeight: 500,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: "1.5rem",
      fontWeight: 500,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: 500,
      lineHeight: 1.3,
    },
    h5: {
      fontSize: "1.125rem",
      fontWeight: 500,
      lineHeight: 1.3,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 500,
      lineHeight: 1.3,
    },
    subtitle1: {
      fontSize: "0.875rem",
      fontWeight: 500,
      lineHeight: 1.3,
    },
    subtitle2: {
      fontSize: "0.750rem",
      fontWeight: 700,
      lineHeight: 1.3,
    },
    caption: {
      fontSize: "0.750rem",
      fontWeight: 400,
      lineHeight: 1.3,
    },
    overline: {
      fontSize: "0.750rem",
      fontWeight: 500,
      lineHeight: 1.3,
    },
    code: {
      fontSize: "1rem",
      lineHeight: 1.5,
      fontWeight: 500,
      fontFamily: "Menlo, Consolas, monospace",
    },
    tag: {
      borderRadius: "3px",
      background: MuiGrey[200],
      padding: "4px 8px",
    },
  },
  palette: {
    ...defaultThemeOptions?.palette,
    border: {
      main: MuiGrey[300],
      hover: MuiGrey[300],
      focus: COLOR_SCALE.PRIMARY[400],
    },
    icon: {
      action: MuiGrey[400],
    },
    subdued: {
      main: MuiGrey[400],
      blue: COLOR_SCALE.PRIMARY[100],
    },
    link: {
      main: COLOR_SCALE.PRIMARY[500],
    },
    dark: {
      main: MuiGrey[800],
      light: MuiGrey[700],
      contrastText: MuiGrey[100],
    },
    inverse: {
      main: MuiGrey[200],
      light: MuiGrey[100],
      contrastText: COLOR_SCALE.PRIMARY[500],
      ...COLOR_SCALE.PRIMARY,
    },
    muted: {
      main: "#F6F6F6",
      contrastText: MuiGrey[600],
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          textTransform: "initial",
          fontWeight: 600,
          fontSize: theme.typography.body2.fontSize,
        }),
        sizeSmall: ({ theme }: { theme: any }) => ({
          fontSize: theme.typography.caption.fontSize,
        }),
      },
      variants: [
        {
          props: { color: "subdued" as any, variant: "contained" },
          style: {
            backgroundColor: MuiGrey[300],
            "&:hover": {
              backgroundColor: MuiGrey[400],
            },
          },
        },
        {
          props: { color: "inverse" as any, variant: "contained" },
          style: {
            "&:disabled": {
              color: MuiGrey[300],
              backgroundColor: MuiGrey[700],
            },
          },
        },
      ],
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ ownerState, theme }: { ownerState: any; theme: any }) => ({
          ...(ownerState.color === "info" && {
            borderRadius: `${DEFAULT_RADIUS_SIZE}px`,
            boxShadow: `white 0px 0px 0px 0px inset, ${MuiGrey[400]} 0px 0px 0px 1px inset, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px`,
            color: MuiGrey[700],
          }),
        }),
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          code: "code",
        } as any,
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: ({ theme }: { theme: any }) => ({
          fontSize: theme.typography.body2.fontSize,
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: ({ theme }: { theme: any }) => ({
          fontSize: theme.typography.body2.fontSize,
        }),
      },
    },
    MuiStep: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          "&.Mui-completed *": {
            color: `${theme.palette.success[600]} !important`,
          },
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }: { theme: any }) => ({
          backgroundColor: theme.palette.common.black,
          fontSize: theme.typography.caption.fontSize,
        }),
      },
    },
    MuiInput: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          fontSize: theme.typography.body2.fontSize,
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          fontSize: theme.typography.body2.fontSize,
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          fontSize: theme.typography.body2.fontSize,
        }),
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState, theme }: { ownerState: any; theme: any }) => ({
          fontSize: theme.typography.body2.fontSize,
          fontWeight: 500,
          ...((ownerState.size as any) === "xsmall" && {
            height: "1.125rem",
            "& .MuiChip-label": {
              paddingLeft: "0.5rem",
              paddingRight: "0.5rem",
            },
          }),
        }),
        sizeSmall: ({ theme }: { theme: any }) => ({
          fontSize: theme.typography.caption.fontSize,
        }),
        colorSuccess: {
          color: COLOR_SCALE.SUCCESS[600],
          background: COLOR_SCALE.SUCCESS[100],
        },
        colorWarning: {
          color: COLOR_SCALE.WARNING[800],
          background: COLOR_SCALE.WARNING[100],
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          position: "relative",
          "& .MuiTouchRipple-root": { display: "none" },
          "& svg": { fontSize: 21 },
          "&:not(.Mui-checked) svg": { opacity: 0 },
          "&:not(.Mui-checked):before": {
            content: '""',
            position: "absolute",
            width: 16,
            height: 16,
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            border: "1px solid",
            borderColor: alpha(MuiGrey[900], 0.25),
            borderRadius: 4,
          },
          "& input": {
            top: "50%",
            left: "50%",
            height: 36,
            width: 36,
            transform: "translate(-50%, -50%)",
          },
          "&:not(.Mui-checked):hover:before": {
            borderColor: COLOR_SCALE.PRIMARY[500],
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          ...theme.typography.h6,
          paddingTop: theme.spacing(3),
          paddingBottom: theme.spacing(0.5),
        }),
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          ...theme.typography.body2,
        }),
      },
    },
  },
} as Parameters<typeof createTheme>[0] & {
  typography?: {
    code?: React.CSSProperties;
    tag?: React.CSSProperties;
  };
  palette?: {
    border?: { main: string; hover: string; focus: string };
    icon?: { action: string };
    subdued?: { main: string; blue: string };
    dark?: { main: string; contrastText: string; light: string };
    inverse?: { main: string; contrastText: string; light: string };
    muted?: { main: string; contrastText: string };
    link?: { main: string };
  };
};

const theme = createTheme(themeOptions);

export default theme;
